import { useState } from "react";
import { sourceCssColor } from "./palette";
import type { PirateSourceDisplayEntry, PirateSourceType } from "./types";

const COUNT_FORMAT = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });

/** Most represented source first; ties keep the canonical source order. */
export function rankPirateSources(entries: PirateSourceDisplayEntry[]): PirateSourceDisplayEntry[] {
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => right.entry.count - left.entry.count || left.index - right.index)
    .map(({ entry }) => entry);
}

export interface PirateSourceSummaryLabels {
  totalCaption: string;
  subtitle: string;
}

/**
 * Summary presentation: the shares, read in rank order.
 *
 *  1. One proportional bar, most represented source on the left. The widget
 *     title already says what is measured, so no caption repeats it.
 *  2. Below it, a compact key that flows from the left in the same order as
 *     the bar. Items take their own width, never equal columns, so no key item
 *     seems to sit under a segment it does not belong to. The share is the
 *     figure that stands out; the count is left to the tooltip.
 *  3. The detection volume closes the key row, right-aligned and muted: context
 *     for the shares, not a headline.
 *
 * The widget is annex context on a host-led page: shares use the 16 px
 * semibold step, below the page's 30 px KPI figures, and the bar is 10 px.
 *
 * Pointing at a segment or a key item quiets the other sources so the pair reads together.
 */
export function PirateSourceSummary({
  entries,
  labels,
}: {
  entries: PirateSourceDisplayEntry[];
  labels: PirateSourceSummaryLabels;
}) {
  const [active, setActive] = useState<PirateSourceType | null>(null);
  const ranked = rankPirateSources(entries);
  const total = entries[0]?.total_count ?? 0;
  const quiet = (source: PirateSourceType) => active !== null && active !== source;
  const detail = (entry: PirateSourceDisplayEntry) =>
    `${entry.label}: ${entry.displayPercentage}% · ${COUNT_FORMAT.format(entry.count)} ${labels.totalCaption}`;

  return (
    <div
      className="density-widget-body flex min-w-0 flex-col gap-2.5"
      onPointerLeave={() => setActive(null)}
    >
      <div
        role="img"
        aria-label={`${labels.subtitle}: ${ranked
          .map((entry) => `${entry.label} ${entry.displayPercentage}%`)
          .join(", ")}`}
        className="flex h-2.5 w-full min-w-0 shrink-0 gap-0.5 overflow-hidden rounded-full bg-surface-elevated"
      >
        {ranked
          .filter((entry) => entry.count > 0)
          .map((entry) => (
            <div
              key={entry.source_type}
              data-source={entry.source_type}
              title={detail(entry)}
              onPointerEnter={() => setActive(entry.source_type)}
              className="h-full min-w-1 transition-[flex-grow,opacity] duration-300 motion-reduce:transition-none"
              style={{
                flex: `${entry.percentage} 1 0%`,
                backgroundColor: sourceCssColor(entry.source_type),
                opacity: quiet(entry.source_type) ? 0.3 : 1,
              }}
            />
          ))}
      </div>

      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-6 gap-y-1.5">
        <dl className="flex min-w-0 flex-wrap items-baseline gap-x-5 gap-y-1.5">
          {ranked.map((entry) => {
            const absent = entry.count === 0;
            return (
              <div
                key={entry.source_type}
                data-source={entry.source_type}
                title={detail(entry)}
                onPointerEnter={() => setActive(absent ? null : entry.source_type)}
                className={`flex min-w-0 items-baseline gap-1.5 whitespace-nowrap transition-opacity duration-200 motion-reduce:transition-none ${
                  absent ? "opacity-50" : quiet(entry.source_type) ? "opacity-45" : ""
                }`}
              >
                <dt className="flex items-center gap-1.5 text-xs font-medium text-secondary">
                  <span
                    aria-hidden
                    className="size-2 shrink-0 self-center rounded-full"
                    style={{
                      backgroundColor: absent ? "var(--border)" : sourceCssColor(entry.source_type),
                    }}
                  />
                  {entry.label}
                </dt>
                <dd className="text-base font-semibold tabular-nums text-primary">
                  {entry.displayPercentage}%
                </dd>
              </div>
            );
          })}
        </dl>
        <p className="ml-auto shrink-0 text-xs tabular-nums text-muted" data-part="total">
          {COUNT_FORMAT.format(total)} {labels.totalCaption}
        </p>
      </div>
    </div>
  );
}
